import { useState } from 'react';
import { useVoiceStore } from '../../stores/voiceStore.js';
import { useAeThex } from '../../components/AeThexProvider.jsx';

const DEMO_SPEAKERS = [
  { id: 's1', name: 'AeThex Dev', avatar: 'A', isSpeaking: true,  isMuted: false, role: 'Host' },
  { id: 's2', name: 'nova',       avatar: 'N', isSpeaking: false, isMuted: false, role: 'Speaker' },
];
const DEMO_AUDIENCE = [
  { id: 'a1', name: 'drift',  avatar: 'D' },
  { id: 'a2', name: 'echo',   avatar: 'E' },
  { id: 'a3', name: 'pulse',  avatar: 'P' },
  { id: 'a4', name: 'axiom',  avatar: 'A' },
  { id: 'a5', name: 'flux',   avatar: 'F' },
  { id: 'a6', name: 'cipher', avatar: 'C' },
];

function SpeakerTile({ p }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '16px 12px', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: `1px solid ${p.isSpeaking ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.07)'}`, minWidth: 110, flex: '0 1 140px', transition: 'border-color 0.2s' }}>
      <div style={{ position: 'relative' }}>
        {p.isSpeaking && (
          <div style={{ position: 'absolute', inset: -4, borderRadius: '50%', border: '2px solid #22c55e', animation: 'stagePulse 1s ease-in-out infinite' }} />
        )}
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: p.role === 'Host' ? 'linear-gradient(135deg,#f59e0b,#ef4444)' : 'linear-gradient(135deg,#6366f1,#4f46e5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#fff' }}>
          {p.avatar}
        </div>
        {p.isMuted && (
          <div style={{ position: 'absolute', bottom: -2, right: -2, width: 18, height: 18, borderRadius: '50%', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, border: '2px solid rgba(10,10,14,0.95)' }}>🔇</div>
        )}
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{p.name}</div>
        <div style={{ fontSize: 10, color: p.role === 'Host' ? '#f59e0b' : '#a5b4fc', fontWeight: 600 }}>{p.role}</div>
      </div>
    </div>
  );
}

function AudienceTile({ p }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#374151,#1f2937)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>
        {p.avatar}
      </div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', maxWidth: 48, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>{p.name}</div>
    </div>
  );
}

export default function StageView({ channel }) {
  const [speakers, setSpeakers] = useState(DEMO_SPEAKERS);
  const [audience, setAudience] = useState(DEMO_AUDIENCE);
  const [requested, setRequested] = useState(false);
  const [liveCount] = useState(speakers.length + audience.length);
  const { user } = useAeThex();

  const isInStage = useVoiceStore(s => s.channelId === channel?.id);
  const joinVoice  = useVoiceStore(s => s.join);
  const leaveVoice = useVoiceStore(s => s.leave);

  const handleRequest = () => {
    setRequested(r => !r);
  };

  const handleJoinAudience = () => {
    joinVoice(channel?.id, channel?.name, user);
    setAudience(a => [
      ...a.filter(p => p.id !== 'me'),
      { id: 'me', name: user?.display_name || user?.username || 'You', avatar: (user?.display_name || user?.username || 'Y')[0].toUpperCase() }
    ]);
  };

  const handleLeave = () => {
    leaveVoice();
    setAudience(a => a.filter(p => p.id !== 'me'));
    setRequested(false);
  };

  return (
    <>
      <style>{`@keyframes stagePulse { 0%,100%{transform:scale(1);opacity:.8} 50%{transform:scale(1.12);opacity:.4} }`}</style>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: 'rgba(6,6,10,0.6)' }}>
        {/* Stage header */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block', animation: 'stagePulse 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 12, fontWeight: 800, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Live</span>
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>🎙️ {channel?.name || 'Stage Channel'}</span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginLeft: 'auto' }}>{liveCount} listeners</span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {/* Speakers section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: 'rgba(255,255,255,0.35)', marginBottom: 12 }}>Speakers — {speakers.length}</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {speakers.map(s => <SpeakerTile key={s.id} p={s} />)}
            </div>
          </div>

          {/* Audience section */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: 'rgba(255,255,255,0.35)', marginBottom: 12 }}>Audience — {audience.length}</div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {audience.map(a => <AudienceTile key={a.id} p={a} />)}
              {audience.length === 0 && <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>No listeners yet</span>}
            </div>
          </div>
        </div>

        {/* Bottom action bar */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
          {!isInStage ? (
            <button onClick={handleJoinAudience}
              style={{ padding: '9px 20px', borderRadius: 8, background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.35)', color: '#22c55e', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              👂 Join as Listener
            </button>
          ) : (
            <>
              <button onClick={handleRequest}
                style={{ padding: '9px 20px', borderRadius: 8, background: requested ? 'rgba(245,158,11,0.15)' : 'rgba(99,102,241,0.15)', border: `1px solid ${requested ? 'rgba(245,158,11,0.4)' : 'rgba(99,102,241,0.35)'}`, color: requested ? '#f59e0b' : '#a5b4fc', fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}>
                {requested ? '✋ Request Sent' : '✋ Request to Speak'}
              </button>
              <button onClick={handleLeave}
                style={{ padding: '9px 16px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Leave
              </button>
            </>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
            {requested ? 'Waiting for host approval…' : isInStage ? 'You\'re listening' : 'Join to participate'}
          </span>
        </div>
      </div>
    </>
  );
}
